import { useState, useTransition } from "react";
import { useNavigate as useRouter } from "../../router";
import { Button } from "../../button";
import { Input } from "../../input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";
import { Badge } from "../../badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select";
import { apiCall } from "../../api/client";
import {
  hiringScorecards,
  type ScorecardTemplateShape,
} from "../../api/contracts/hiring-scorecards";

interface Props {
  templates: ScorecardTemplateShape[];
}

const QUESTION_KINDS: Array<"rating" | "text" | "yes_no"> = [
  "rating",
  "text",
  "yes_no",
];

export function ScorecardTemplateSettings({ templates }: Props) {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [newTpl, setNewTpl] = useState({ slug: "", name: "" });
  const [newQ, setNewQ] = useState<
    Record<number, { slug: string; prompt: string; kind: "rating" | "text" | "yes_no" }>
  >({});

  function setQ(tid: number, patch: Partial<{ slug: string; prompt: string; kind: "rating" | "text" | "yes_no" }>) {
    setNewQ((prev) => {
      const existing = prev[tid] ?? {
        slug: "",
        prompt: "",
        kind: "rating" as const,
      };
      return { ...prev, [tid]: { ...existing, ...patch } };
    });
  }

  function run<T>(fn: () => Promise<T>) {
    setError(null);
    startTransition(async () => {
      try {
        await fn();
        router.refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Action failed.");
      }
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="type-title">Scorecard templates</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {templates.map((t) => (
          <Card key={t.id} className="gap-3 py-3">
            <CardContent className="space-y-2 px-3">
            <div className="flex items-center justify-between">
              <div className="type-body text-foreground">
                {t.name}
                {t.is_default ? (
                  <Badge variant="outline" className="ml-2 type-body">
                    default
                  </Badge>
                ) : null}
              </div>
              <span className="type-body text-muted-foreground">
                {t.questions.length} question
                {t.questions.length === 1 ? "" : "s"}
              </span>
            </div>
            <ul className="flex flex-col">
              {t.questions.map((q, idx) => (
                <li
                  key={q.id}
                  className={
                    idx === t.questions.length - 1
                      ? "flex items-center justify-between gap-2 py-2 first:pt-0"
                      : "flex items-center justify-between gap-2 border-b border-border/40 py-2 first:pt-0"
                  }
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className="capitalize">
                      {q.kind.replace("_", "/")}
                    </Badge>
                    <span className="type-body text-foreground truncate">
                      {q.prompt}
                    </span>
                  </div>
                  <Button
                    size="xs"
                    variant="ghost"
                    disabled={pending}
                    onClick={() =>
                      run(() =>
                        apiCall(hiringScorecards.questionsArchive, {
                          params: { id: t.id, questionId: q.id },
                        }),
                      )
                    }
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
            <div className="grid grid-cols-3 gap-2 items-end pt-1">
              <Input
                placeholder="Question prompt"
                value={newQ[t.id]?.prompt ?? ""}
                onChange={(e) => setQ(t.id, { prompt: e.target.value })}
              />
              <Input
                placeholder="slug"
                value={newQ[t.id]?.slug ?? ""}
                onChange={(e) => setQ(t.id, { slug: e.target.value })}
              />
              <div className="flex gap-2">
                <Select
                  value={newQ[t.id]?.kind ?? "rating"}
                  onValueChange={(v) =>
                    setQ(t.id, { kind: v as "rating" | "text" | "yes_no" })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_KINDS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  disabled={
                    pending ||
                    !newQ[t.id]?.prompt ||
                    !newQ[t.id]?.slug
                  }
                  onClick={() => {
                    const draft = newQ[t.id];
                    if (!draft) return;
                    run(() =>
                      apiCall(hiringScorecards.questionsAdd, {
                        params: { id: t.id },
                        body: {
                          slug: draft.slug,
                          prompt: draft.prompt,
                          kind: draft.kind,
                        },
                      }),
                    );
                    setNewQ((prev) => ({
                      ...prev,
                      [t.id]: { slug: "", prompt: "", kind: "rating" },
                    }));
                  }}
                >
                  Add question
                </Button>
              </div>
            </div>
            </CardContent>
          </Card>
        ))}
        <div className="grid grid-cols-3 gap-2 items-end">
          <Input
            placeholder="New template name"
            value={newTpl.name}
            onChange={(e) => setNewTpl({ ...newTpl, name: e.target.value })}
          />
          <Input
            placeholder="slug"
            value={newTpl.slug}
            onChange={(e) => setNewTpl({ ...newTpl, slug: e.target.value })}
          />
          <Button
            size="sm"
            disabled={pending || !newTpl.name || !newTpl.slug}
            onClick={() => {
              run(() =>
                apiCall(hiringScorecards.templatesCreate, {
                  body: { slug: newTpl.slug, name: newTpl.name },
                }),
              );
              setNewTpl({ slug: "", name: "" });
            }}
          >
            Add template
          </Button>
        </div>
        {error ? <p className="type-body text-destructive">{error}</p> : null}
      </CardContent>
    </Card>
  );
}
